import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import './AllGroups.css';
import LocationFilter from '../locaties/LocationFilter.js';

const validYears = [
  '2023-2024',
  '2024-2025',
  '2025-2026',
  '2026-2027',
  '2027-2028',
  '2028-2029'
];

function getCurrentSchoolYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  // Schooljaar loopt van september (9) tot augustus (8)
  if (month >= 8) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
}

function AllGroups() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();
  const userRole = userInfo?.role;

  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(getCurrentSchoolYear());

  const fetchGroups = useCallback(() => {
    setIsLoading(true);

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/group/all`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {
        setGroups(response.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching groups', error);
        setIsLoading(false);
      });
  }, [token]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'docent') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => console.error('Error fetching locations', err));
    } else {
      setLocations([]);
    }
  }, [userRole, token]);

  const handleDelete = (id) => {
    if (window.confirm('Weet je zeker dat je deze groep wilt verwijderen?')) {
      axios
        .delete(`${process.env.REACT_APP_BACKEND_URL}/group/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(() => fetchGroups())
        .catch((error) => console.error('Fout bij het verwijderen van de groep', error));
    }
  };

  const handleStudentClick = (student_id) => {
    navigate(`/viewstudent/${student_id}`);
  };

  const handleDocentClick = (docent_id) => {
    navigate(`/viewdocent/${docent_id}`);
  };

  const displayedGroups = groups
    .filter((group) => {
      if (locationFilter === 'all') return true;
      if (locationFilter === 'none') return !group.location_id;
      return group.location_id === locationFilter;
    })
    .filter((group) => {
      if (yearFilter === 'all') return true;
      return group.school_year === yearFilter;
    });

  const handleDeleteRemovedStudent = async (groepId, studentId) => {
    if (!window.confirm('Definitief verwijderen uit groep-historie?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/group/deleteStudentFromGroupHistory`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { groep_id: groepId, student_id: studentId }
        }
      );
      fetchGroups();
    } catch (err) {
      console.error(err);
      alert('Verwijderen mislukt.');
    }
  };

  return (
    <div className="all-groups">
      <div className="header d-flex align-items-center justify-content-between mb-3">
        {userRole === 'docent' ? (
          <h1>Overzicht Van Jouw Groep(en)</h1>
        ) : (
          <h1>Overzicht Van Alle Groepen</h1>
        )}

        {(userRole === 'super_admin' || userRole === 'locatie_admin') && (
          <Link to="/newgroup" className="btn btn-success">
            <i className="bi bi-plus-circle me-2"></i>Nieuwe groep
          </Link>
        )}
      </div>

      <div className="d-flex mb-3">
        <LocationFilter
          userRole={userRole}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          locations={locations}
        />

        <div className="form-group mb-3 d-inline-block ms-3">
          <label htmlFor="yearFilter" className="me-2">
            Filter op Schooljaar:
          </label>
          <select
            id="yearFilter"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="form-select"
          >
            <option value="all">Alle jaren</option>
            {validYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : displayedGroups.length > 0 ? (
        <div className="groups-container">
          {displayedGroups.map((group) => (
            <div className="group-sticky-note" key={group.groep_id}>
              <h3>{group.groep_name}</h3>
              <div className="small text-secondary mb-2">
                {group.location_name || 'Zonder locatie'} • {group.school_year}
              </div>
              <hr />

              <div>
                <h4>Groepsdocent:</h4>
                <ul>
                  {group.docenten && group.docenten.length > 0 ? (
                    group.docenten.map((docent) => (
                      <li key={docent.docent_id}>
                        {userRole === 'super_admin' || userRole === 'locatie_admin' ? (
                          <span
                            className="docent-name text-dark cursor-pointer"
                            onClick={() => handleDocentClick(docent.docent_id)}
                          >
                            {docent.firstname} {docent.lastname}
                          </span>
                        ) : (
                          <span className="docent-name">
                            {docent.firstname} {docent.lastname}
                          </span>
                        )}
                      </li>
                    ))
                  ) : (
                    <li>
                      <span className="docent-name">Geen docenten toegevoegd</span>
                    </li>
                  )}
                </ul>
              </div>

              <div>
                {group.students.length > 0 && (
                  <>
                    <h4>Actief:</h4>
                    <ul>
                      {[...group.students]
                        .sort((a, b) => new Date(a.joined_date) - new Date(b.joined_date))
                        .map((s, idx) => (
                          <li key={s.student_id}>
                            {idx + 1}.{' '}
                            <span
                              className="text-dark cursor-pointer"
                              onClick={() => handleStudentClick(s.student_id)}
                            >
                              {s.firstname} {s.lastname}
                            </span>{' '}
                            ({new Date(s.joined_date).toLocaleDateString()})
                          </li>
                        ))}
                    </ul>
                  </>
                )}

                {group.removedStudents.length > 0 && (
                  <>
                    <h4>Uitgeschreven:</h4>
                    <ul>
                      {[...group.removedStudents]
                        .sort((a, b) => new Date(a.exit_date) - new Date(b.exit_date))
                        .map((s) => (
                          <li
                            key={s.student_id}
                            className="text-muted d-flex justify-content-between align-items-center"
                          >
                            <span
                              className="cursor-pointer"
                              onClick={() => handleStudentClick(s.student_id)}
                            >
                              {s.firstname} {s.lastname} (
                              {new Date(s.exit_date).toLocaleDateString()})
                            </span>

                            {(userRole === 'super_admin' || userRole === 'locatie_admin') && (
                              <i
                                className="bi bi-trash3 text-danger cursor-pointer ms-3"
                                title="Definitief verwijderen uit groep-historie"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRemovedStudent(group.groep_id, s.student_id);
                                }}
                              />
                            )}
                          </li>
                        ))}
                    </ul>
                  </>
                )}
              </div>

              {(userRole === 'super_admin' || userRole === 'locatie_admin') && (
                <div className="actions">
                  <Link
                    to={`/editgroup/${group.groep_id}`}
                    className="bi bi-pencil edit-icon"
                  ></Link>
                  <i
                    className="bi bi-trash3 delete-icon"
                    onClick={() => handleDelete(group.groep_id)}
                  ></i>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p>Geen groepen gevonden</p>
      )}

      <div className="spacer-bottom"></div>
    </div>
  );
}

export default AllGroups;